import React, { useState, useRef, useEffect } from 'react';
import { fragaBirger } from '../qlik';

// ── FRÅGA BIRGER ──────────────────────────────────────────────────
// Flytande följeslagare (döpt efter grundaren Birger Myhr): rund knapp
// nere till höger (granne med nattläget) → flytande chattvy. Backend är
// en agent-loop som själv läser portalens verktyg (veckorapport, trend,
// beläggning, pace …) innan den svarar. Kräver admin/ledning — knappen
// renderas inte alls för andra.

const BIRGER_BLA = '#1B2A4A';

// Gör länkar klickbara: markdown [text](url) och rena http(s)-URL:er
function LankadText({ text }) {
  const delar = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()"]+[^\s<>()".,!?;:])/g;
  let sist = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > sist) delar.push(text.slice(sist, m.index));
    const url = m[2] || m[3];
    const label = m[1] || m[3].replace(/^https?:\/\/(www\.)?/, '');
    delar.push(
      <a key={delar.length} href={url} target="_blank" rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}>
        {label}
      </a>
    );
    sist = re.lastIndex;
  }
  if (sist < text.length) delar.push(text.slice(sist));
  return <>{delar}</>;
}

export default function Birger({ nivaFull }) {
  const [open, setOpen] = useState(false);
  const [medd, setMedd] = useState([]);   // [{roll:'user'|'assistant', text, verktyg?}]
  const [input, setInput] = useState('');
  const [jobb, setJobb] = useState(false);
  const [fel, setFel] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [medd, jobb, open]);

  const skicka = async (fardigText) => {
    const text = (fardigText || input).trim();
    if (!text || jobb) return;
    setFel(''); setJobb(true); setInput('');
    const nya = [...medd, { roll: 'user', text }];
    setMedd(nya);
    try {
      const res = await fragaBirger({ meddelanden: nya.map((m) => ({ roll: m.roll, text: m.text })) });
      setMedd((prev) => [...prev, { roll: 'assistant', text: res.svar, verktyg: res.verktyg || [] }]);
    } catch (e) {
      setFel(e.message || 'Birger svarade inte — försök igen');
    } finally { setJobb(false); }
  };

  const VERKTYGSNAMN = {
    veckorapport: 'veckorapporten', dagens_lage: 'dagens läge', belaggning: 'beläggningen',
    realtid_drift: 'driftläget', period_analys: 'periodanalys', booking_pace: 'bokningstakten',
    nya_bokningar: 'nya bokningar', topp_produkter: 'toppprodukter', trend: 'trenden',
    brasserie_dag_kvall: 'dag/kväll-splitten', web_search: 'webben',
  };

  const FORSLAG = nivaFull
    ? ['Hur går det för oss just nu?',
       'Hur ser bokningsläget ut för kommande veckor?',
       'Vad händer i Bruksvallarna och Funäsfjällen denna vecka?']
    : ['Hur gick det för min avdelning förra veckan?',
       'Vad händer i Bruksvallarna och Funäsfjällen denna vecka?',
       'En gäst frågar om fisket — vad kan jag tipsa om?'];

  return (
    <>
      {/* Flytande knapp — granne med nattläget */}
      <button onClick={() => setOpen((o) => !o)} data-noprint="1"
        title="Fråga Birger" aria-label="Fråga Birger"
        style={{
          position: 'fixed', bottom: 28, right: 214, zIndex: 900,
          width: 36, height: 36, borderRadius: '50%',
          background: open ? '#4A3B2A' : BIRGER_BLA, color: '#fff', border: 'none',
          fontFamily: 'Georgia, serif', fontSize: 17, fontWeight: 700, fontStyle: 'italic',
          cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.24)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        B
      </button>

      {/* Flytande chattvy */}
      {open && (
        <div data-noprint="1" style={{
          position: 'fixed', bottom: 76, right: 24, zIndex: 950,
          width: 'min(430px, calc(100vw - 32px))', maxHeight: 'min(600px, calc(100vh - 120px))',
          display: 'flex', flexDirection: 'column',
          background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)',
          borderRadius: 14, boxShadow: '0 10px 40px rgba(0,0,0,0.35)', overflow: 'hidden',
        }}>
          <div style={{ background: BIRGER_BLA, color: '#fff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'Georgia, serif' }}>
              <span style={{ fontStyle: 'italic', fontWeight: 700, fontSize: 15 }}>Birger</span>
              <span style={{ fontSize: 11, opacity: 0.75, marginLeft: 8, fontFamily: 'system-ui,sans-serif' }}>läser portalens siffror åt dig</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {medd.length > 0 && (
                <button onClick={() => { setMedd([]); setFel(''); }} title="Börja om"
                  style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
                  Ny tråd
                </button>
              )}
              <button onClick={() => setOpen(false)} title="Stäng"
                style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 15, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 120 }}>
            {medd.length === 0 && !jobb && (
              <div style={{ fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 10 }}>
                  {nivaFull
                    ? 'Tjena! Fråga mig om verksamheten — jag hämtar färska siffror ur portalen (och söker på webben) innan jag svarar.'
                    : 'Tjena! Fråga mig om vad som händer i fjällen, gästtips eller om Bruksvallsliden — jag kollar upp det åt dig.'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {FORSLAG.map((f) => (
                    <button key={f} onClick={() => skicka(f)}
                      style={{ textAlign: 'left', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 12.5, cursor: 'pointer', color: 'var(--text)', fontFamily: 'system-ui,sans-serif' }}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {medd.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.roll === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '88%', borderRadius: 10, padding: '7px 11px',
                background: m.roll === 'user' ? BIRGER_BLA : 'var(--surface-2)',
                color: m.roll === 'user' ? '#fff' : 'var(--text)',
                fontSize: 12.5, fontFamily: 'system-ui,sans-serif', whiteSpace: 'pre-wrap', lineHeight: 1.5,
              }}>
                {m.roll === 'assistant' ? <LankadText text={m.text} /> : m.text}
                {m.verktyg?.length ? (
                  <div style={{ fontSize: 10, marginTop: 5, opacity: 0.65 }}>
                    läste: {m.verktyg.map((v) => VERKTYGSNAMN[v] || v).join(', ')}
                  </div>
                ) : null}
              </div>
            ))}
            {jobb && <div style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>🤔 Birger läser siffrorna…</div>}
            {fel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>{fel}</div>}
          </div>

          <div style={{ display: 'flex', gap: 6, padding: 10, borderTop: '1px solid var(--border)', alignItems: 'flex-end' }}>
            <textarea value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); skicka(); } }}
              placeholder="Fråga Birger…" rows={1}
              style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 8, padding: 8, fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'none', maxHeight: 90 }} />
            <button onClick={() => skicka()} disabled={jobb || !input.trim()}
              style={{ background: input.trim() && !jobb ? BIRGER_BLA : 'transparent', color: input.trim() && !jobb ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
              Skicka
            </button>
          </div>
        </div>
      )}
    </>
  );
}
